// Manage Account slice — the data seam for the Manage screen. Lives centrally in
// store/slices (one slice file per page). The thunk is parameterized by
// `accountId` and never decides WHICH account that is — the caller (the hook)
// passes the id resolved from the auth slice. That's what makes multi-plan
// switching a drop-in: change the selected id, re-dispatch, done.
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getManageAccount, getOverview } from '../../services/api';
import { mapManageAPIToUI } from '../../components/modules/manage-account/manage-account.adapters';

// ----- Thunk: the backend seam -----
// Fetches the levers (/manage-account) and, for the hero only, the matching
// /overview plan card (balance/vested/status). The overview call is the seam the
// multi-plan PR replaces with a selector against its own plan-cards slice.
// Always live — the frontend mock is gone (fixture data is the backend's
// data/manage_account.json via scripts/generate_data.py; the wire-shaped test
// fixture lives in src/test-utils/fixtures/manage-account.mock.js).
export const fetchManageAccount = createAsyncThunk(
  'manageAccount/fetch',
  async (accountId, { getState, rejectWithValue }) => {
    try {
      // Identity + accounts come from the app-global auth slice (the single
      // identity store). participant = { participantId, firstName, lastName }.
      const auth = getState().auth;
      const participant = auth.participant;
      const account = auth.accounts?.find((a) => a.accountId === accountId) ?? null;

      const [manage, overview] = await Promise.all([
        getManageAccount(accountId), // core data — its failure rejects the thunk
        // Hero enrichment only — best-effort so a failure degrades the hero
        // (balance → "—") without taking down the rest of the page.
        participant?.participantId
          ? getOverview(participant.participantId).catch(() => null)
          : Promise.resolve(null),
      ]);
      const card = overview?.plans?.find((p) => p.accountId === accountId) ?? null;

      return {
        accountId,
        data: mapManageAPIToUI({ manage, card, identity: participant, account }),
      };
    } catch (err) {
      // Normalized client errors are {status, code, message}; thunk errors are plain.
      return rejectWithValue(err.message || 'Failed to load your account');
    }
  },
  // Dedupe (single-fetch guard): skip when the SAME account is already in flight OR
  // already loaded. This thunk fans out to TWO endpoints (/manage-account + /overview),
  // so an un-guarded double-fire (StrictMode mount→unmount→remount, or a post-resolve
  // remount) doubles BOTH. Switching the selected account (new accountId), or a retry
  // after failure, still proceeds. `lastArg` is the in-flight arg (set in `pending`);
  // `accountId` only updates on success, so it can't gate the in-flight window. See
  // FRONTEND_RULES §7 (single-fetch guard).
  {
    condition: (accountId, { getState }) => {
      const s = getState().manageAccount;
      if ((s.status === 'loading' || s.status === 'succeeded') && s.lastArg === accountId) return false;
      return true;
    },
  },
);

const initialState = {
  data: null,
  accountId: null, // which account `data` belongs to (guards against stale renders)
  status: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
  error: null,
  lastArg: undefined, // the accountId the current/in-flight fetch is for — drives the single-fetch guard
};

const manageAccountSlice = createSlice({
  name: 'manageAccount',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchManageAccount.pending, (state, action) => {
        state.status = 'loading';
        state.error = null;
        state.lastArg = action.meta.arg; // record which account this fetch is for
        // If we're switching accounts, drop stale data so the skeleton shows.
        if (action.meta.arg !== state.accountId) state.data = null;
      })
      .addCase(fetchManageAccount.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.accountId = action.payload.accountId;
        state.data = action.payload.data;
      })
      .addCase(fetchManageAccount.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Failed to load your account';
      });
  },
});

// ----- Selectors -----
export const selectManageAccountData = (state) => state.manageAccount.data;
export const selectManageAccountStatus = (state) => state.manageAccount.status;
export const selectManageAccountError = (state) => state.manageAccount.error;
export const selectManageAccountAccountId = (state) => state.manageAccount.accountId;

export default manageAccountSlice.reducer;
